import { FC } from 'react';
import {
  BorderLinearProgress,
  DashboardContainer,
  ContainerContent,
  DashboardContainerHeader,
  DashboardContainerBody,
  ContainerScroll,
  ContainerSubText,
  ContainersWrapper,
  DividerContainer,
  H2,
  H3,
  ResolvedText,
  StyledTitle,
} from '../MoaDashboard.styles';
import { ExtractionsContainerProps } from './ExtractionsContainer.types';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DashboardMetricsDetails } from '../../../services/dashboard/dashboard.types';
import { ITooltipParams } from 'ag-grid-community';

const ExtractionsContainer: FC<ExtractionsContainerProps> = ({
  inProgress,
  upNext,
  done,
  setDrawerColumnDefs,
  setOpenDrawer,
  setDrawerType,
  setFetchExtractionsFailure,
  setExtractionAndActivationsJobId,
  setRequestUserName,
}) => {
  const muiTheme = createTheme();
  const extractionsColumnDefs = [
    {
      minWidth: 80,
      maxWidth: 100,
      field: 'label',
      headerName: 'Store#',
      sortable: true,
      floatingFilter: false,
    },
    {
      minWidth: 80,
      maxWidth: 100,
      field: 'marketNo',
      headerName: 'FZ Code',
      sortable: false,
      floatingFilter: false,
    },
    {
      minWidth: 400,
      field: 'reason',
      headerName: 'Error Reason',
      sortable: false,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value,
    },
    {
      minWidth: 80,
      maxWidth: 100,
      field: 'isResolved',
      headerName: '',
      sortable: false,
      floatingFilter: false,
      cellRenderer: (params: any) => {
        if (params.value) {
          return <ResolvedText>Resolved</ResolvedText>;
        } else {
          return '';
        }
      },
    },
  ];

  const couponsColumnDefs = [
    {
      minWidth: 100,
      field: 'label',
      headerName: 'Coupon Group',
      sortable: true,
      floatingFilter: false,
    },
    {
      minWidth: 400,
      field: 'reason',
      headerName: 'Error Reason',
      sortable: false,
      floatingFilter: false,
      tooltipValueGetter: (params: ITooltipParams) => params.value,
    },
  ];

  const defineDrawer = (progressMetrics: DashboardMetricsDetails) => {
    if (progressMetrics.extractType === 'MANINV' || progressMetrics.extractType === 'STOREREF') {
      return;
    }
    if (progressMetrics.extractType === 'COUPONS') {
      setDrawerColumnDefs(couponsColumnDefs);
      setDrawerType('couponsExtractions');
      setExtractionAndActivationsJobId(progressMetrics.jobId);
      setOpenDrawer(true);
      setFetchExtractionsFailure(true);
    } else {
      setDrawerColumnDefs(extractionsColumnDefs);
      setFetchExtractionsFailure(true);
      setOpenDrawer(true);
      setDrawerType('extract');
      setExtractionAndActivationsJobId(progressMetrics.jobId);
      setRequestUserName(progressMetrics.userName);
    }
  };

  const renderStatus = (progressMetrics: DashboardMetricsDetails) => {
    const resolved = Number(progressMetrics.resolved);
    if (progressMetrics.failed === 0 || progressMetrics.failed === null) {
      return null;
    }

    if (progressMetrics.failed > resolved) {
      return (
        <>
          <ErrorOutlineIcon
            data-testid={'failedIcon-' + progressMetrics.jobId}
            fontSize='medium'
            aria-label='Incomplete'
            style={{
              color: 'red',
              marginLeft: 'auto',
              alignContent: 'flex-end',
              paddingRight: '5px',
              paddingTop: '3px',
            }}
          />
          <H3
            dataTestId={'failedLink-' + progressMetrics.jobId}
            label={progressMetrics.failed - resolved + ' failed'}
            to={''}
            style={{ paddingLeft: '0' }}
            onClick={() => defineDrawer(progressMetrics)}
          />
        </>
      );
    }
    return (
      <>
        <H3
          dataTestId={'resolvedText-' + progressMetrics.jobId}
          label={progressMetrics.resolved + ' resolved'}
          to={''}
          style={{ paddingLeft: '0' }}
          onClick={() => defineDrawer(progressMetrics)}
        />
      </>
    );
  };

  const renderInProgress = () => {
    if (inProgress.length === 0) {
      return (
        <ContainerScroll data-testid='extractEmptyContainer' style={{ height: '160px', alignItems: 'baseline' }}>
          <StyledTitle>No extractions in progress</StyledTitle>
        </ContainerScroll>
      );
    } else {
      return (
        <ContainerScroll data-testid='extractInProgressContainer' style={{ height: '160px' }}>
          {inProgress.map((inProgressMetrics, index: number) => {
            return (
              <div key={'extraction-progress-' + index}>
                <StyledTitle>
                  Processing {inProgressMetrics.extractType} for {inProgressMetrics.userName}
                </StyledTitle>
                <ContainerContent style={{ position: 'relative' }}>
                  <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
                    <H2
                      style={{
                        paddingTop: '4px',
                        paddingLeft: '5px',
                        position: 'absolute',
                        left: '15px',
                        zIndex: 2,
                        fontWeight: 600,
                      }}
                    >
                      {inProgressMetrics.processed}
                    </H2>
                    <BorderLinearProgress
                      variant='determinate'
                      data-testid={'extractProgressBar-' + inProgressMetrics.jobId}
                      value={
                        inProgressMetrics.processed ? (inProgressMetrics.processed / inProgressMetrics.total) * 100 : 0
                      }
                      style={{ width: '60%' }}
                    />
                  </ThemeProvider>
                  <H2 style={{ paddingTop: '4px', paddingLeft: '5px', fontWeight: 600 }}>{inProgressMetrics.total}</H2>
                  {renderStatus(inProgressMetrics)}
                </ContainerContent>
              </div>
            );
          })}
        </ContainerScroll>
      );
    }
  };

  const renderUpNext = () => {
    if (upNext.length === 0) {
      return (
        <ContainerScroll style={{ height: '110px', alignItems: 'baseline' }}>
          <StyledTitle>No extractions waiting</StyledTitle>
        </ContainerScroll>
      );
    } else {
      return (
        <ContainerScroll data-testid='upNextExtractContainer' style={{ height: '110px' }}>
          {upNext.map((upNextMetrics, index: number) => {
            return (
              <div
                data-testid={'upNextExtract-' + upNextMetrics.jobId + '-row'}
                style={{ flexDirection: 'row', display: 'flex', justifyContent: 'space-between' }}
                key={'extraction-next-' + index}
              >
                <ContainersWrapper data-testid={'upNextExtract-' + upNextMetrics.jobId + '-total'}>
                  <H2
                    style={{
                      justifyContent: 'center',
                      alignItems: 'end',
                      paddingTop: '10px',
                      paddingLeft: '20px',
                      width: '10px',
                      fontWeight: 600,
                    }}
                  >
                    {upNextMetrics.total}
                  </H2>
                </ContainersWrapper>
                <div
                  style={{
                    justifyContent: 'space-between',
                    alignItems: 'end',
                    flexDirection: 'row',
                    display: 'flex',
                    flexWrap: 'nowrap',
                    overflow: 'hidden',
                    width: '70%',
                  }}
                >
                  <ContainerSubText data-testid={'upNextExtract-' + upNextMetrics.jobId + '-type'}>
                    {upNextMetrics.extractType.toLowerCase()}
                  </ContainerSubText>
                  <ContainerSubText
                    data-testid={'upNextExtract-' + upNextMetrics.jobId + '-user'}
                    style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
                  >
                    {upNextMetrics.userName}
                  </ContainerSubText>
                </div>
              </div>
            );
          })}
        </ContainerScroll>
      );
    }
  };

  const renderDone = () => {
    if (done.length === 0) {
      return (
        <ContainerScroll style={{ height: '150px', alignItems: 'baseline' }}>
          <StyledTitle>No extractions</StyledTitle>
        </ContainerScroll>
      );
    } else {
      return (
        <ContainerScroll data-testid='doneExtractContainer' style={{ height: '150px' }}>
          {done.map((doneMetrics, index: number) => {
            return (
              <div
                data-testid={'doneExtract-' + doneMetrics.jobId + '-row'}
                style={{ flexDirection: 'row', display: 'flex', justifyContent: 'space-between' }}
                key={'extraction-done-' + index}
              >
                <ContainersWrapper>
                  <H2
                    data-testid={'doneExtract-' + doneMetrics.jobId + '-total'}
                    style={{
                      justifyContent: 'center',
                      alignItems: 'end',
                      paddingTop: '10px',
                      paddingLeft: '20px',
                      width: '40px',
                      fontWeight: 600,
                    }}
                  >
                    {doneMetrics.total}
                  </H2>
                  {renderStatus(doneMetrics)}
                </ContainersWrapper>
                <div
                  style={{
                    justifyContent: 'space-between',
                    alignItems: 'end',
                    flexDirection: 'row',
                    display: 'flex',
                    flexWrap: 'nowrap',
                    overflow: 'hidden',
                    width: '70%',
                  }}
                >
                  <ContainerSubText data-testid={'doneExtract-' + doneMetrics.jobId + '-type'}>
                    {doneMetrics.extractType.toLowerCase()}
                  </ContainerSubText>
                  <ContainerSubText
                    data-testid={'doneExtract-' + doneMetrics.jobId + '-user'}
                    style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
                  >
                    {doneMetrics.userName}
                  </ContainerSubText>
                </div>
              </div>
            );
          })}
        </ContainerScroll>
      );
    }
  };

  return (
    <DashboardContainer data-test={'extractionsContainer'} style={{ minHeight: '400px', maxHeight: '600px' }}>
      <DashboardContainerHeader data-testid='extractionsContainerHeader'>Extractions</DashboardContainerHeader>
      <DashboardContainerBody>
        {renderInProgress()}
        <DividerContainer>
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            <Divider orientation='horizontal' variant={'fullWidth'} flexItem />
          </ThemeProvider>
        </DividerContainer>
        <StyledTitle>Up next</StyledTitle>
        {renderUpNext()}
        <DividerContainer>
          <ThemeProvider theme={muiTheme} key={'time-picker-mui-theme'}>
            <Divider orientation='horizontal' variant={'fullWidth'} flexItem />
          </ThemeProvider>
        </DividerContainer>
        <StyledTitle>Done</StyledTitle>
        {renderDone()}
      </DashboardContainerBody>
    </DashboardContainer>
  );
};

export default ExtractionsContainer;
